import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {map, takeUntil, throttleTime} from 'rxjs/operators';

import {OverlayService} from '../../services/overlay/overlay.service';
import {Overlay} from '../../services/overlay/Overlay';
import {OverlayType2} from '../../services/overlay/OverlayType2';
import {CesiumAdapterService} from '@src/app/services/cesiumAdapter21/cesium-adapter.service';
import {GlobalEventsService} from '@src/app/services/global-events.service';
import {MapPosition} from '@src/app/models/MapPosition';

@Component({
  selector: 'app-layouts-tab',
  templateUrl: './layouts-tab.component.html',
  styleUrls: ['./layouts-tab.component.scss'],
})
export class LayoutsTabComponent implements OnInit, OnDestroy {
  readonly sensitivityFactor = 5;
  readonly sensArr = [
    0.000005, 0.00001, 0.00005, 0.0001, 0.0005, 0.0008, 0.0009, 0.001, 0.002,
    0.003,
  ];
  canUse: boolean;
  defaultOverlayStep = this.sensArr[4];
  overlayImages: Overlay[] = [];
  overlaysType2: OverlayType2[] = [];
  isCads$: Observable<boolean>;
  isShowControls = false;
  isEnabledOverlayControls = false;
  overlayStep = this.defaultOverlayStep;
  isKeyboardMovementEnabled = false;
  defaultVisibleItem: {id: number; opacity: number} | undefined;
  showUploadInputType = false;
  selectedFile: File = null;
  isOwnerOfEvent = false;
  isThrottlingReposition = false;

  private unsubscribe$ = new Subject<void>();
  activeOverlay: Overlay | undefined;
  private initialOverlaySettings: {
    topLat?: number;
    leftLng?: number;
    bottomLat?: number;
    rightLng?: number;
    opacity?: number;
    rotation?: number;
  } = {};

  private repositionActionSubject = new Subject<{
    topLat: number;
    leftLng: number;
    bottomLat: number;
    rightLng: number;
  }>();

  constructor(
    private overlayService: OverlayService,
    private cesiumAdapterService: CesiumAdapterService,
    private globalEventsService: GlobalEventsService,
    private cdr: ChangeDetectorRef
  ) {}

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.isKeyboardMovementEnabled) {
      if (event.key === 'ArrowUp') {
        this.onOverlayArrowClick('up');
      } else if (event.key === 'ArrowDown') {
        this.onOverlayArrowClick('down');
      } else if (event.key === 'ArrowLeft') {
        this.onOverlayArrowClick('left');
      } else if (event.key === 'ArrowRight') {
        this.onOverlayArrowClick('right');
      }
    }
  }

  ngOnInit() {
    this.canUse = window['CAN_SELF_IMPORT_CAD'];

    this.observeRepositionAction();
    this.observeOverlayUndoAction();

    this.overlayService
      .getOverlayImages()
      .subscribe((overlayImgs: Overlay[]) => {
        this.overlayImages = overlayImgs;
      });
    this.overlayService
      .getOverlaysType2()
      .subscribe((overlayImgs: OverlayType2[]) => {
        this.overlaysType2 = overlayImgs;
      });
    this.isCads$ = this.overlayService.getFloorLayouts().pipe(
      map((overlays: any[]) => {
        return overlays.length > 0;
      })
    );
    this.loadIsEventOwner();

    this.overlayService.activeOverlayReset$.subscribe(() => {
      this.resetOverlaySettings();
      this.overlayService.unmarkAsLayoutUnsaved(this.activeOverlay.getId());
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onToggleOverlayItemVisibility(event: {id: number; isVisible: boolean}) {
    if (this.activeOverlay && this.overlayService.hasUnsavedLayoutChanges()) {
      this.resetOverlaySettings();
      this.overlayService.unmarkAsLayoutUnsaved(this.activeOverlay.getId());
    }

    this.isEnabledOverlayControls = event.isVisible;
    this.activeOverlay = this.overlayImages.find(
      overlay => overlay.getId() === event.id
    );

    if (event.isVisible) {
      this.activeOverlay.moveCameraIntoView();
    }

    this.setActiveOverlayAsInitial();
  }

  onOverlayStepChange(event: number) {
    this.overlayStep = this.sensArr[event - 1];
  }

  onRenameOverlay(event: {overlay: Overlay; newName: string}): void {
    event.overlay.name = event.newName;

    this.overlayService
      .updateImageOverlay(event.overlay, window['EVENT_ID'])
      .then();
  }

  onItemClicked(overlay: Overlay): void {
    if (!overlay.isVisible || overlay.id === this.activeOverlay.id) {
      overlay.moveCameraIntoView();
      return;
    }

    if (!this.hideControls()) {
      return;
    }

    this.onToggleOverlayItemVisibility({
      id: overlay.getId(),
      isVisible: true,
    });
  }

  adjustOverlay(action: 'expand' | 'collapse'): void {
    const sensitivity = (this.sensArr.indexOf(this.overlayStep) + 0.5) * 3;
    // Calculate the center point
    const centerLat =
      (this.activeOverlay.left_top_lat + this.activeOverlay.right_bottom_lat) /
      2;
    const centerLon =
      (this.activeOverlay.left_top_lon + this.activeOverlay.right_bottom_lon) /
      2;

    // Calculate the current width and height in degrees
    const width =
      this.activeOverlay.right_bottom_lon - this.activeOverlay.left_top_lon;
    const height =
      this.activeOverlay.left_top_lat - this.activeOverlay.right_bottom_lat;

    // Calculate the change based on step value
    const widthChange = width * (sensitivity / 100);
    const heightChange = height * (sensitivity / 100);

    let newWidth: number;
    let newHeight: number;

    if (action === 'expand') {
      newWidth = width + widthChange;
      newHeight = height + heightChange;
    } else {
      // 'collapse'
      newWidth = width - widthChange;
      newHeight = height - heightChange;
    }

    // Compute the new coordinates
    const newLeftTopLat = centerLat + newHeight / 2;
    const newLeftTopLon = centerLon - newWidth / 2;
    const newRightBottomLat = centerLat - newHeight / 2;
    const newRightBottomLon = centerLon + newWidth / 2;

    if (
      newLeftTopLat > 90 ||
      newRightBottomLat < -90 ||
      newLeftTopLon < -180 ||
      newRightBottomLon > 180
    ) {
      // Return if out of bounds
      return;
    }

    if (this.isThrottlingReposition) {
      return;
    }

    this.repositionActionSubject.next({
      topLat: newLeftTopLat,
      leftLng: newLeftTopLon,
      bottomLat: newRightBottomLat,
      rightLng: newRightBottomLon,
    });
  }

  onKeyboardMovementButtonClick(): void {
    this.isKeyboardMovementEnabled = !this.isKeyboardMovementEnabled;

    // Prevent scrolling when keyboard movement is enabled (move focus away)
    document.getElementById('es-cesium-container').focus();
  }

  onOverlayArrowClick(arrow: 'left' | 'right' | 'up' | 'down') {
    if (this.isThrottlingReposition) {
      return;
    }
    switch (arrow) {
      case 'left':
        this.repositionActionSubject.next({
          topLat: this.activeOverlay.left_top_lat,
          leftLng: this.activeOverlay.left_top_lon - this.overlayStep,
          bottomLat: this.activeOverlay.right_bottom_lat,
          rightLng: this.activeOverlay.right_bottom_lon - this.overlayStep,
        });
        break;
      case 'right':
        this.repositionActionSubject.next({
          topLat: this.activeOverlay.left_top_lat,
          leftLng: this.activeOverlay.left_top_lon + this.overlayStep,
          bottomLat: this.activeOverlay.right_bottom_lat,
          rightLng: this.activeOverlay.right_bottom_lon + this.overlayStep,
        });
        break;
      case 'up':
        this.repositionActionSubject.next({
          topLat: this.activeOverlay.left_top_lat + this.overlayStep,
          leftLng: this.activeOverlay.left_top_lon,
          bottomLat: this.activeOverlay.right_bottom_lat + this.overlayStep,
          rightLng: this.activeOverlay.right_bottom_lon,
        });
        break;
      case 'down':
        this.repositionActionSubject.next({
          topLat: this.activeOverlay.left_top_lat - this.overlayStep,
          leftLng: this.activeOverlay.left_top_lon,
          bottomLat: this.activeOverlay.right_bottom_lat - this.overlayStep,
          rightLng: this.activeOverlay.right_bottom_lon,
        });
        break;
      default:
        console.log('unknown direction');
        break;
    }
  }

  saveChanges(): void {
    this.setActiveOverlayAsInitial();
    this.overlayService
      .updateImageOverlay(this.activeOverlay, window['EVENT_ID'])
      .then(result => {
        // Update the overlay cache
        this.overlayService.removeFromVisibleLayouts(this.activeOverlay);
        this.overlayService.addToVisibleLayouts(this.activeOverlay);

        this.overlayService.unmarkAsLayoutUnsaved(this.activeOverlay.getId());
        this.isKeyboardMovementEnabled = false;
        this.globalEventsService.ToastNotificationEvent.emit({
          message: 'Successfully updated the overlay.',
          autoHideDelay: 2000,
          title: 'Success',
          classes: ['notification'],
        });
      })
      .catch(error => {
        console.error('catch updateImageOverlay error ', error);
      });
  }

  opacityChanged(value: number) {
    this.activeOverlay.opacity = value;
  }

  isNewOverlayDifferentThanInitial(): boolean {
    return (
      this.activeOverlay.left_top_lat !== this.initialOverlaySettings.topLat ||
      this.activeOverlay.left_top_lon !== this.initialOverlaySettings.leftLng ||
      this.activeOverlay.right_bottom_lat !==
        this.initialOverlaySettings.bottomLat ||
      this.activeOverlay.right_bottom_lon !==
        this.initialOverlaySettings.rightLng ||
      this.activeOverlay.opacity !== this.initialOverlaySettings.opacity ||
      this.activeOverlay.rotation !== this.initialOverlaySettings.rotation
    );
  }

  onFileSelected(event) {
    this.selectedFile = event.target.files[0] as File;

    const img = new Image();
    img.src = URL.createObjectURL(this.selectedFile); // Adjusted this line to create a temporary URL for the file

    img.onload = () => {
      const centerMapPos: MapPosition = this.getMapCenter();

      const bottomRightMapPos = this.pixelsToLatLong(
        img.width,
        img.height,
        centerMapPos.latitude,
        centerMapPos.longitude,
        1 / 2500 // 1 mile per 2500 pixels
      );

      const overlay = {
        left_top_lat: centerMapPos.latitude,
        left_top_lon: centerMapPos.longitude,
        right_bottom_lat: bottomRightMapPos.latitude,
        right_bottom_lon: bottomRightMapPos.longitude,
        opacity: 1,
      };

      const reader = new FileReader();

      reader.readAsDataURL(this.selectedFile);
      reader.onload = () => {
        const imageData = reader.result.toString().split(',')[1];

        this.overlayService
          .addImageOverlay(
            overlay,
            window['EVENT_ID'],
            this.selectedFile.name,
            imageData
          )
          .then((result: any) => {
            const parsedOverlay = JSON.parse(result).data;
            const newOverlay = {
              left_top_lat: parsedOverlay.left_top_lat,
              left_top_lon: parsedOverlay.left_top_lon,
              right_bottom_lat: parsedOverlay.right_bottom_lat,
              right_bottom_lon: parsedOverlay.right_bottom_lon,
              imageUrl: parsedOverlay.imageUrl,
              id: parsedOverlay.id,
              rotation: parsedOverlay.rotation,
              sort: 0,
              status: parsedOverlay.status,
              group: parsedOverlay.group,
              is_floor_layout: parsedOverlay.is_floor_layout,
              isVisible: false,
              isDrawn: false,
              opacity: parsedOverlay.initial_opacity,
              name: parsedOverlay.name,
              image: parsedOverlay.image,
            };

            this.defaultVisibleItem = {
              id: newOverlay.id,
              opacity: newOverlay.opacity,
            };

            this.globalEventsService.ToastNotificationEvent.emit({
              message: 'Successfully added the overlay.',
              autoHideDelay: 2000,
              title: 'Success',
              classes: ['notification'],
            });

            this.overlayService.addOverlayWithoutServerUpdate(newOverlay);

            setTimeout(() => {
              // Hide current active overlay
              if (this.activeOverlay) {
                this.onToggleOverlayItemVisibility({
                  id: this.activeOverlay.id,
                  isVisible: false,
                });
              }

              this.activeOverlay = this.overlayImages.find(
                ov => ov.getId() === newOverlay.id
              );
              this.activeOverlay.draw();
              this.setActiveOverlayAsInitial();
              this.isEnabledOverlayControls = true;
              this.selectedFile = null;
              this.showUploadInputType = false;
              this.overlayService.addToVisibleLayouts(this.activeOverlay);
              this.cdr.detectChanges();
            }, 0);
          })
          .catch(errorResp => {
            this.selectedFile = null;
            this.showUploadInputType = false;
            this.cdr.detectChanges();

            if (!errorResp.error) {
              return;
            }

            let err = errorResp.error;
            if (typeof err === 'string') {
              err = JSON.parse(errorResp.error);
            }

            this.globalEventsService.ToastNotificationEvent.emit({
              message: err.message,
              autoHideDelay: 2000,
              title: 'Layout upload error',
              classes: ['error'],
            });
          });
      };
    };
  }

  deleteOverlayImage(overlay: Overlay) {
    if (!confirm('Are you sure you want to delete this overlay image?')) {
      return;
    }

    this.overlayService
      .deleteImageOverlay(overlay.getId(), window['EVENT_ID'])
      .then(() => {
        overlay.isVisible = false;

        this.overlayService.deleteOverlay(overlay);

        if (this.activeOverlay.id === overlay.id) {
          this.isEnabledOverlayControls = false;
          this.activeOverlay.getEntityIds().forEach((value, key) => {
            this.cesiumAdapterService.removeOverlay(value);
          });
          this.activeOverlay = null;
          this.initialOverlaySettings = {};
        }

        this.globalEventsService.ToastNotificationEvent.emit({
          message: 'Successfully deleted the overlay.',
          autoHideDelay: 2000,
          title: 'Success',
          classes: ['notification'],
        });
      })
      .catch(error => {
        console.error('catch deleteImageOverlay error ', error);
      });
  }

  rotate(isLeft: boolean): void {
    const degrees = 0.5 * (isLeft ? -1 : 1);
    this.activeOverlay.rotation = this.activeOverlay.getRotation() + degrees;

    this.repositionActionSubject.next({
      topLat: this.activeOverlay.left_top_lat,
      leftLng: this.activeOverlay.left_top_lon,
      bottomLat: this.activeOverlay.right_bottom_lat,
      rightLng: this.activeOverlay.right_bottom_lon,
    });
  }

  hideControls(): boolean {
    if (
      this.isNewOverlayDifferentThanInitial() &&
      !confirm(
        'You have not saved the changes made to the overlay. Proceed anyway?'
      )
    ) {
      return false;
    }

    if (this.isNewOverlayDifferentThanInitial()) {
      this.resetOverlaySettings();
      this.overlayService.unmarkAsLayoutUnsaved(this.activeOverlay.getId());
    }

    this.isShowControls = false;
    return true;
  }

  private observeRepositionAction(): void {
    const throttleTimeInMilliseconds = 300;
    this.repositionActionSubject
      .pipe(
        throttleTime(throttleTimeInMilliseconds),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({topLat, leftLng, bottomLat, rightLng}) => {
        this.repositionOverlay(topLat, leftLng, bottomLat, rightLng);
        this.isThrottlingReposition = true;

        // Tell our service there is unsaved changes
        this.overlayService.unmarkAsLayoutUnsaved(this.activeOverlay.getId());
        if (this.isNewOverlayDifferentThanInitial()) {
          this.overlayService.markAsLayoutUnsaved(this.activeOverlay.getId());
        }
        setTimeout(() => {
          this.isThrottlingReposition = false;
        }, throttleTimeInMilliseconds);
      });
  }

  private observeOverlayUndoAction(): void {
    this.overlayService
      .getOverlayUndo()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (
          this.isNewOverlayDifferentThanInitial() &&
          confirm('Are you sure you want to reset overlay settings?')
        ) {
          this.resetOverlaySettings();
        }
      });
  }

  private resetOverlaySettings(): void {
    this.activeOverlay.rotation = this.initialOverlaySettings.rotation;
    this.repositionOverlay(
      this.initialOverlaySettings.topLat,
      this.initialOverlaySettings.leftLng,
      this.initialOverlaySettings.bottomLat,
      this.initialOverlaySettings.rightLng
    );
  }

  private repositionOverlay(topLat, leftLng, bottomLat, rightLng) {
    if (!this.overlayStep || this.overlayStep <= 0) {
      this.overlayStep = this.defaultOverlayStep;
      return;
    }

    const updatedOverlay: Overlay = Object.assign(
      Object.create(Object.getPrototypeOf(this.activeOverlay)),
      this.activeOverlay
    );
    updatedOverlay.left_top_lat = topLat;
    updatedOverlay.left_top_lon = leftLng;
    updatedOverlay.right_bottom_lat = bottomLat;
    updatedOverlay.right_bottom_lon = rightLng;

    this.activeOverlay.getEntityIds().forEach((value, key) => {
      this.cesiumAdapterService.removeOverlay(value);
    });

    updatedOverlay.drawFromImageUrl(this.activeOverlay.imageUrl, [
      topLat,
      leftLng,
      bottomLat,
      rightLng,
    ]);

    this.replaceActiveOverlay(updatedOverlay);
    this.activeOverlay = updatedOverlay;
  }

  private replaceActiveOverlay(updatedOverlay: Overlay) {
    const index = this.overlayImages.findIndex(
      overlay => overlay === this.activeOverlay
    );

    if (index !== -1) {
      this.overlayImages[index] = updatedOverlay;
    } else {
      console.error('activeOverlay not found in overlayImages');
    }

    this.defaultVisibleItem = {
      id: updatedOverlay.getId(),
      opacity: updatedOverlay.opacity,
    };
  }

  private setActiveOverlayAsInitial() {
    this.initialOverlaySettings = {
      topLat: this.activeOverlay.left_top_lat,
      leftLng: this.activeOverlay.left_top_lon,
      bottomLat: this.activeOverlay.right_bottom_lat,
      rightLng: this.activeOverlay.right_bottom_lon,
      opacity: this.activeOverlay.opacity,
      rotation: this.activeOverlay.rotation,
    };
  }

  private loadIsEventOwner(): void {
    const events = window['EVENT_LIST'];
    for (const key in events) {
      if (events.hasOwnProperty(key)) {
        if (key === window['EVENT_ID'].toString()) {
          this.isOwnerOfEvent = events[key].client_id === window['CLIENT_ID'];
        }
      }
    }
  }

  private getMapCenter(): MapPosition {
    const viewer = this.cesiumAdapterService.viewer;
    const camPos = viewer.camera.positionCartographic;
    return new MapPosition({
      latitude: camPos.latitude * (180 / Math.PI),
      longitude: camPos.longitude * (180 / Math.PI),
    });
  }

  private pixelsToLatLong(
    xPixels: number,
    yPixels: number,
    startLat: number,
    startLong: number,
    milesPerPixel: number
  ): MapPosition {
    const latPerMile = 1 / 69.172; // Approximate latitude degrees per mile
    const longPerMile = 1 / (69.172 * Math.cos((startLat * Math.PI) / 180)); // Approximate longitude degrees per mile

    const lat = startLat - yPixels * milesPerPixel * latPerMile;
    const long = startLong + xPixels * milesPerPixel * longPerMile;

    return new MapPosition({latitude: lat, longitude: long});
  }
}
